// CRound — 一局狀態機
// 對應 PRD §5 一局狀態機、§3.2.1 Game Core
//
// 設計原則：
// - 純資料 + 同步狀態變化；不依賴 Phaser、無 setTimeout、無 async
// - 所有狀態變化透過 IEventBus 對外發事件
// - 玩家 / AI 透過 submitAction(seat, action) 推進狀態
// - CRound 不負責 UI 計時、AI 思考延遲（caller 端控制）
//
// 狀態流（PRD §5.1）：
//   init → deal → turn(seat) ↔ waiting_reaction → meld_resolved
//                                   ↓                  ↓
//                              round_end_hu        turn(next seat)
//   wall 抽完 → round_end_draw
//
// 順位攔胡：
//   多家可胡時從出牌者下家開始逐家詢問；第一位胡牌者成立。

import type { IEventBus } from '@event-bus/event-bus';

import type { TPlayerAction, TRoundEventMap, TRoundSummary } from './events';
import {
  addMeld,
  addToConcealed,
  addToFlowers,
  createEmptyHand,
  removeFromConcealed,
  type THand,
} from './hand';
import { canHu } from './hu';
import type { TMeld } from './meld';
import { orderedMeldReactionSeats, orderedReactionSeats } from './reaction-order';
import { resolveReactionPriority } from './round-resolve';
import { computeHuResults, SpecialWin, type THuInfo } from './round-settle';
import {
  findDiscardReactions,
  findKongAddedOption,
  findKongConcealedOptions,
  type TDiscardReaction,
  type TKongAddedOption,
  type TKongConcealedOption,
} from './ruleset';
import { findTenpaiTiles } from './tenpai';
import { isFlower, type TTile } from './tile';
import type { TRng, TSeat, TWind } from './types';
import {
  createWall,
  drawLiveTile,
  drawReplacementTile,
  liveWallRemaining,
  recordCompletedKong,
  type TWall,
} from './wall';
import { PlayerActionType, RoundSummaryKind } from '@core/events';
import { MeldType } from '@core/meld';

// ============================================================
// 對外型別
// ============================================================

/** 一局的階段 const。對外語意見 TRoundPhase 各值註解。 */
export const RoundPhase = {
  Init: 'init',
  Deal: 'deal',
  // 階段式開局的補花階段（發完牌、各家手上還有花，逐張補替補；UI 動畫用）
  Flower: 'flower',
  // 補花完成後、莊家摸第一張前，從莊家起依序詢問四家起手暗槓
  InitialKong: 'initial_kong',
  // 某家 turn（PRD 的 TURN_PLAYER / TURN_AI 不再分；用 currentSeat 區分）
  Turn: 'turn',
  // 某家剛出牌，等其他 3 家反應
  WaitingReaction: 'waiting_reaction',
  RoundEndHu: 'round_end_hu',
  RoundEndDraw: 'round_end_draw',
} as const;

export type TRoundPhase = (typeof RoundPhase)[keyof typeof RoundPhase];

export type TRoundConfig = {
  /** 當局莊家位。 */
  readonly dealerSeat: TSeat;
  /** 當局圈風。 */
  readonly prevailingWind: TWind;
  /** 依開門家推導的各家門風。 */
  readonly seatWinds: readonly [TWind, TWind, TWind, TWind];
  /** 玩家固定位置。 */
  readonly playerSeat: TSeat;
  /** 當局開門家；省略時保留舊測試從洗牌首張發牌的行為。 */
  readonly openingSeat?: TSeat;
  /** 開門家數完骰值後，下一個開始抓取的墩位（0-based）。 */
  readonly openingStackIndex?: number;
  /** 72 墩環狀牌牆的實際起抓位置。 */
  readonly wallStartStackIndex?: number;
  /** 莊家連莊次數 n（連 N 拉 N → 2n+1 台）；省略視為 0（沒連莊）。 */
  readonly dealerStreak?: number;
};

export type TRoundState = {
  wall: TWall;
  hands: [THand, THand, THand, THand];
  discards: [TTile[], TTile[], TTile[], TTile[]];
  config: TRoundConfig;
  currentSeat: TSeat;
  phase: TRoundPhase;
  declaredTenpai: Set<TSeat>;
  /** 當前 turn 家最近一次摸進的牌（含槓後補牌）；自摸胡時即為 winTile。非 turn 階段為上一手殘值。 */
  lastDrawnTile: TTile | null;
  /** lastDrawnTile 是誰摸的（伺服器遮蔽用：輪到誰不代表那張是誰的）。 */
  lastDrawnSeat: TSeat | null;
  /** 最近一次摸進的牌是否來自「槓後補牌」（槓上開花判定）。一般摸牌會設回 false。 */
  lastDrawWasKong: boolean;
  /** 等待反應時的捨牌資訊（waiting_reaction 階段有值） */
  pendingDiscard: { seat: TSeat; tile: TTile } | null;
  /** 加槓宣告正等待其他家搶槓胡；全過後才真正把碰升級成槓。 */
  pendingAddedKong: { seat: TSeat; option: TKongAddedOption } | null;
  /** 目前正被詢問的反應者；胡與一般副露都只會有一席。 */
  pendingReactionSeats: Set<TSeat>;
  /** 各家當下合法的反應清單 + 是否可胡（waiting_reaction 期間有值）。用於驗證收到的動作合法性，並避免重算 canHu。 */
  pendingReactions: Map<TSeat, { reactions: readonly TDiscardReaction[]; canHu: boolean }>;
  /** 已收到的反應（seat → action） */
  receivedReactions: Map<TSeat, TPlayerAction>;
  /** 依距離排好的胡牌候選；逐家詢問，不同時開啟選單。 */
  pendingHuSeats: TSeat[];
  /** 目前詢問到 pendingHuSeats 的索引；等於 length 表示已進一般副露階段。 */
  pendingHuIndex: number;
  /** 依「碰 > 槓 > 吃、同類按座位距離」排好的副露候選；逐家詢問。 */
  pendingMeldSeats: TSeat[];
  /** 目前詢問到 pendingMeldSeats 的索引。 */
  pendingMeldIndex: number;
  /** 起手暗槓已完成檢查的座位數；0..4，由莊家起依序推進。 */
  initialKongChecksCompleted: number;
};

// ============================================================
// 工具函式
// ============================================================

/** 下一個 seat（出牌權傳遞）。 */
const nextSeat = (s: TSeat): TSeat => ((s + 1) % 4) as TSeat;
const nextSeatByDistance = (seat: TSeat, distance: number): TSeat =>
  ((seat + distance) % 4) as TSeat;

// ============================================================
// CRound 主類別
// ============================================================

export class CRound {
  private state: TRoundState;
  private readonly bus: IEventBus<TRoundEventMap>;

  constructor(config: TRoundConfig, rng: TRng, bus: IEventBus<TRoundEventMap>) {
    this.bus = bus;

    const wall =
      config.openingSeat === undefined
        ? createWall(rng)
        : createWall(rng, {
            openingSeat: config.openingSeat,
            firstStackIndex: config.openingStackIndex ?? 0,
            ...(config.wallStartStackIndex === undefined
              ? {}
              : { wallStartStackIndex: config.wallStartStackIndex }),
          });
    this.state = {
      wall,
      hands: [createEmptyHand(), createEmptyHand(), createEmptyHand(), createEmptyHand()],
      discards: [[], [], [], []],
      config,
      currentSeat: config.dealerSeat,
      phase: RoundPhase.Init,
      declaredTenpai: new Set(),
      lastDrawnTile: null,
      lastDrawnSeat: null,
      lastDrawWasKong: false,
      pendingDiscard: null,
      pendingAddedKong: null,
      pendingReactionSeats: new Set(),
      pendingReactions: new Map(),
      receivedReactions: new Map(),
      pendingHuSeats: [],
      pendingHuIndex: 0,
      pendingMeldSeats: [],
      pendingMeldIndex: 0,
      initialKongChecksCompleted: 0,
    };
  }

  // ----------------------------------------------------------
  // 公開查詢介面
  // ----------------------------------------------------------

  getPhase(): TRoundPhase {
    return this.state.phase;
  }

  getState(): Readonly<TRoundState> {
    return this.state;
  }

  /** 私有流程呼叫後重新讀取 phase，避免呼叫端依賴 TypeScript 過期的控制流窄化。 */
  private isRoundEnded(): boolean {
    return (
      this.state.phase === RoundPhase.RoundEndHu || this.state.phase === RoundPhase.RoundEndDraw
    );
  }

  /** 某座位是否已宣告聽牌。 */
  hasDeclaredTenpai(seat: TSeat): boolean {
    return this.state.declaredTenpai.has(seat);
  }

  /**
   * 宣告聽牌（玩家在出牌後、確認進入聽牌模式時呼叫）。
   * 與 submitAction 的 'declare-tenpai' 動作不同：此法不限 phase / currentSeat，
   * 因為聽牌宣告發生在「出牌之後」（出牌權已交給下一家）。
   * 記入 declaredTenpai（影響計分聽牌台）並發 tenpai.declared 事件；重複呼叫無作用。
   */
  declareTenpai(seat: TSeat): void {
    if (this.state.declaredTenpai.has(seat)) return;
    this.state.declaredTenpai.add(seat);
    this.bus.emit('tenpai.declared', { seat });
  }

  /** 序列化整個 CRound 狀態（PRD §16.3 跨日續局預留接口）。 */
  serialize(): string {
    return JSON.stringify({
      ...this.state,
      declaredTenpai: [...this.state.declaredTenpai],
      pendingReactionSeats: [...this.state.pendingReactionSeats],
      receivedReactions: [...this.state.receivedReactions.entries()],
      pendingHuSeats: [...this.state.pendingHuSeats],
      pendingMeldSeats: [...this.state.pendingMeldSeats],
    });
  }

  // ----------------------------------------------------------
  // 啟動：發牌 + 補花 + 開始第一個 turn
  // ----------------------------------------------------------

  start(): void {
    if (this.state.phase !== RoundPhase.Init) return;

    this.bus.emit('round.start', {
      dealerSeat: this.state.config.dealerSeat,
      seatWinds: this.state.config.seatWinds,
      prevailingWind: this.state.config.prevailingWind,
    });

    this.state.phase = RoundPhase.Deal;
    const eightFlowersWin = this.dealInitialHands(false); // 即時補花（一步到位，給測試 / 逐步模式用）

    this.bus.emit('wall.dealt', {
      remaining: liveWallRemaining(this.state.wall),
      hands: [this.state.hands[0], this.state.hands[1], this.state.hands[2], this.state.hands[3]],
    });

    if (eightFlowersWin) {
      this.endRoundEightFlowers(eightFlowersWin.seat, eightFlowersWin.winTile);
      return;
    }

    // 一步到位 API 不停在起手詢問；自動完成四家所有暗槓後，仍保證莊家開始 turn。
    this.resolveInitialKongsAutomatically();
    if (this.isRoundEnded()) return;

    // 莊家進入第一個 turn（先抓 1 張變 17 張，再決定動作）
    this.beginTurn(this.state.config.dealerSeat);
  }

  // ----------------------------------------------------------
  // 階段式開局（給 UI 入場動畫 + 補花動畫用）
  //   dealStaged() → (UI 入場提示) → replaceNextFlower() × N → beginPlay()
  // 與 start() 互斥：start() 一步到位（即時補花 + 開打），dealStaged 把補花拆成可動畫的步驟。
  // ----------------------------------------------------------

  /** 階段式：發 16 張（含花、不即時補），發完 wall.dealt，進入 Flower（補花）階段。 */
  dealStaged(): void {
    if (this.state.phase !== RoundPhase.Init) return;

    this.bus.emit('round.start', {
      dealerSeat: this.state.config.dealerSeat,
      seatWinds: this.state.config.seatWinds,
      prevailingWind: this.state.config.prevailingWind,
    });

    this.state.phase = RoundPhase.Deal;
    this.dealInitialHands(true); // 含花發牌：花留在 concealed，等補花階段逐張替換

    this.bus.emit('wall.dealt', {
      remaining: liveWallRemaining(this.state.wall),
      hands: [this.state.hands[0], this.state.hands[1], this.state.hands[2], this.state.hands[3]],
    });

    this.state.phase = RoundPhase.Flower;
  }

  /**
   * 補花一步：依座位序找下一張花（seat 0→3，同家先補完再換下一家），
   * 把它移到 flowers + 摸一張替補（替補若仍是花，留待下一次呼叫處理），
   * 發 tile.flower-replaced + tile.drawn 事件。回 null = 全部補完。
   * UI 連續呼叫並在每步之間插入延遲，即可呈現「各家輪流補花、依序摸牌」。
   */
  replaceNextFlower(): {
    seat: TSeat;
    flower: TTile;
    replacement: TTile | null;
  } | null {
    if (this.state.phase !== RoundPhase.Flower) return null;

    for (let s = 0; s < 4; s++) {
      const seat = s as TSeat;
      const hand = this.state.hands[seat];
      const flower = hand.concealed.find((t) => isFlower(t));
      if (!flower) continue;

      // 移除花 → 放進 flowers 區
      this.state.hands[seat] = addToFlowers(removeFromConcealed(hand, flower), flower);
      this.bus.emit('tile.flower-replaced', { seat, flower });

      if (this.hasEightFlowers(seat)) {
        this.endRoundEightFlowers(seat, flower);
        return { seat, flower, replacement: null };
      }

      // 摸一張替補（替補本身可能又是花，下一次呼叫會再處理）
      const r = drawReplacementTile(this.state.wall);
      if (!r) {
        this.endRoundDraw();
        return null;
      }
      const [newWall, replacement] = r;
      this.state.wall = newWall;
      this.state.hands[seat] = addToConcealed(this.state.hands[seat], replacement);
      this.bus.emit('tile.drawn', {
        seat,
        tile: replacement,
        remaining: liveWallRemaining(this.state.wall),
      });

      return { seat, flower, replacement };
    }

    return null; // 各家手上都沒有花了
  }

  /** 是否還有任一家手上有未替換的花（補花預覽用：發到有花才開始跑動畫）。 */
  hasUnreplacedFlowers(): boolean {
    return this.state.hands.some((h) => h.concealed.some((t) => isFlower(t)));
  }

  /** 補花完成 → 先處理四家起手暗槓，再由莊家進入第一個 turn。 */
  beginPlay(): void {
    if (this.state.phase !== RoundPhase.Flower && this.state.phase !== RoundPhase.Deal) return;
    this.beginInitialKongPhase();
  }

  /**
   * 發初始 16 張 + 補花。
   *
   * 發牌規則：由莊家開始，依出牌方向每家抓 4 張，共 4 輪。
   * 階段式開局把花留在手上；一步到位路徑則在發完後從牌尾補花。
   */
  private dealInitialHands(keepFlowers: boolean): { seat: TSeat; winTile: TTile } | null {
    for (let packet = 0; packet < 4; packet++) {
      for (let distance = 0; distance < 4; distance++) {
        const seat = nextSeatByDistance(this.state.config.dealerSeat, distance);
        for (let tileIndex = 0; tileIndex < 4; tileIndex++) {
          const r = drawLiveTile(this.state.wall);
          if (!r) throw new Error('Live wall depleted during initial deal');
          const [newWall, tile] = r;
          this.state.wall = newWall;
          this.state.hands[seat] = addToConcealed(this.state.hands[seat], tile);
        }
      }
    }

    if (keepFlowers) return null;

    for (let seatValue = 0; seatValue < 4; seatValue++) {
      const seat = seatValue as TSeat;
      while (true) {
        const flower = this.state.hands[seat].concealed.find((tile) => isFlower(tile));
        if (!flower) break;
        this.state.hands[seat] = addToFlowers(
          removeFromConcealed(this.state.hands[seat], flower),
          flower
        );
        this.bus.emit('tile.flower-replaced', { seat, flower });

        if (this.hasEightFlowers(seat)) {
          return { seat, winTile: flower };
        }

        const r = drawReplacementTile(this.state.wall);
        if (!r) throw new Error('Wall depleted during initial flower replacement');
        const [newWall, tile] = r;
        this.state.wall = newWall;
        this.state.hands[seat] = addToConcealed(this.state.hands[seat], tile);
      }
    }
    return null;
  }

  // ----------------------------------------------------------
  // 起手暗槓：補花完成後，從莊家起依序檢查四家
  // ----------------------------------------------------------

  /** 正式 UI 路徑：停在每一位有暗槓候選的玩家，等待其選擇槓或放棄。 */
  private beginInitialKongPhase(): void {
    this.state.phase = RoundPhase.InitialKong;
    this.state.currentSeat = this.state.config.dealerSeat;
    this.state.initialKongChecksCompleted = 0;
    this.offerNextInitialKongDecision();
  }

  /** `start()` 專用：不等待 UI，依序把四家現有的起手暗槓全部完成。 */
  private resolveInitialKongsAutomatically(): void {
    this.state.phase = RoundPhase.InitialKong;
    this.state.initialKongChecksCompleted = 0;
    for (let distance = 0; distance < 4; distance++) {
      const seat = nextSeatByDistance(this.state.config.dealerSeat, distance);
      this.state.currentSeat = seat;
      while (true) {
        const option = findKongConcealedOptions(this.state.hands[seat])[0];
        if (!option) break;
        this.performInitialConcealedKong(seat, option);
        if (this.isRoundEnded()) return;
      }
      this.state.initialKongChecksCompleted += 1;
    }
  }

  /** 略過沒有候選的座位；遇到候選就發 action.available，四家完成後莊家開打。 */
  private offerNextInitialKongDecision(): void {
    while (this.state.initialKongChecksCompleted < 4) {
      const seat = nextSeatByDistance(
        this.state.config.dealerSeat,
        this.state.initialKongChecksCompleted
      );
      this.state.currentSeat = seat;
      if (findKongConcealedOptions(this.state.hands[seat]).length > 0) {
        this.bus.emit('action.available', {
          seat,
          reactions: [],
          canHu: false,
        });
        return;
      }
      this.state.initialKongChecksCompleted += 1;
    }
    this.beginTurn(this.state.config.dealerSeat);
  }

  /** 起手暗槓階段只接受暗槓或放棄；暗槓後同一家會重新檢查，可能連槓。 */
  private handleInitialKongAction(seat: TSeat, action: TPlayerAction): void {
    if (seat !== this.state.currentSeat) {
      throw new Error(`Initial kong action from non-current seat ${seat}`);
    }
    if (action.type === PlayerActionType.Pass) {
      this.state.initialKongChecksCompleted += 1;
      this.offerNextInitialKongDecision();
      return;
    }
    if (action.type !== PlayerActionType.KongConcealed) {
      throw new Error(`Unsupported initial kong action: ${action.type}`);
    }

    const legal = findKongConcealedOptions(this.state.hands[seat]);
    const isLegal = legal.some((option) =>
      option.handTiles.every((tile, index) => tile.id === action.option.handTiles[index]?.id)
    );
    if (!isLegal) throw new Error(`Illegal initial concealed kong from seat ${seat}`);

    this.performInitialConcealedKong(seat, action.option);
    if (this.isRoundEnded()) return;

    if (findKongConcealedOptions(this.state.hands[seat]).length > 0) {
      this.bus.emit('action.available', { seat, reactions: [], canHu: false });
      return;
    }
    this.state.initialKongChecksCompleted += 1;
    this.offerNextInitialKongDecision();
  }

  /** 套用一組起手暗槓並從補牌端補一張；補到花會持續補到非花或八仙過海。 */
  private performInitialConcealedKong(seat: TSeat, option: TKongConcealedOption): void {
    let hand = this.state.hands[seat];
    for (const tile of option.handTiles) hand = removeFromConcealed(hand, tile);
    const meld: TMeld = {
      type: MeldType.KongConcealed,
      tiles: [...option.handTiles],
    };
    this.state.hands[seat] = addMeld(hand, meld);
    this.state.wall = recordCompletedKong(this.state.wall);
    this.bus.emit('meld.formed', { seat, meld });

    const replacement = this.drawWithFlowerReplacement(seat, true);
    if (replacement === null) {
      if (!this.isRoundEnded()) this.endRoundDraw();
      return;
    }
    this.state.hands[seat] = addToConcealed(this.state.hands[seat], replacement);
    this.bus.emit('tile.drawn', {
      seat,
      tile: replacement,
      remaining: liveWallRemaining(this.state.wall),
    });
  }

  // ----------------------------------------------------------
  // 開始某家的 turn：抓 1 張 + 補花 + 列出可用動作
  // ----------------------------------------------------------

  private beginTurn(seat: TSeat): void {
    this.state.currentSeat = seat;
    this.state.phase = RoundPhase.Turn;

    // 抓牌（補花直到非花）
    const drawnResult = this.drawWithFlowerReplacement(seat);
    if (drawnResult === null) {
      if (this.isRoundEnded()) return;
      // 牌山摸完仍要抓 → 流局
      this.endRoundDraw();
      return;
    }
    const drawnTile = drawnResult;

    this.state.hands[seat] = addToConcealed(this.state.hands[seat], drawnTile);
    this.bus.emit('tile.drawn', {
      seat,
      tile: drawnTile,
      remaining: liveWallRemaining(this.state.wall),
    });

    // 列出此家當下可做的動作（自摸胡 / 暗槓 / 加槓 / 出牌）
    this.emitTurnActions(seat, drawnTile);
  }

  /**
   * 抓一張非花牌（途中遇到花就替補）。
   * 牌山抽完還沒抓到非花就回 null（caller 應觸發流局）。
   */
  private drawWithFlowerReplacement(seat: TSeat, fromKong = false): TTile | null {
    let drawFromReplacementEnd = fromKong;
    while (true) {
      const r = drawFromReplacementEnd
        ? drawReplacementTile(this.state.wall)
        : drawLiveTile(this.state.wall);
      if (!r) return null;
      const [newWall, tile] = r;
      this.state.wall = newWall;

      if (!isFlower(tile)) {
        // 記住最近摸進的牌：自摸胡時即為 winTile（取代舊的「掃 concealed 猜一張」）
        this.state.lastDrawnTile = tile;
        this.state.lastDrawnSeat = seat;
        // 記住是否槓後補牌（槓上開花判定）；一般摸牌 fromKong=false 會清掉旗標
        this.state.lastDrawWasKong = fromKong;
        return tile;
      }

      this.state.hands[seat] = addToFlowers(this.state.hands[seat], tile);
      this.bus.emit('tile.flower-replaced', { seat, flower: tile });
      if (this.hasEightFlowers(seat)) {
        this.endRoundEightFlowers(seat, tile);
        return null;
      }
      drawFromReplacementEnd = true;
    }
  }

  /** 列出 turn 階段該家的可用動作。 */
  private emitTurnActions(seat: TSeat, drawnTile: TTile): void {
    const hand = this.state.hands[seat];
    const canSelfHu = canHu(hand, drawnTile, true);
    // 暗槓 / 加槓 候選（MVP 不顯示在 reactions 裡，由 UI 直接從 hand 判讀）
    // 為簡化先只用 reactions 表達「對方捨牌的反應動作」，turn 動作由外部直接組

    this.bus.emit('action.available', {
      seat,
      reactions: [], // turn 階段沒有 reaction（reaction 是給其他家用的）
      canHu: canSelfHu,
    });
  }

  // ----------------------------------------------------------
  // 接收玩家動作（包含 turn 動作與 reaction 動作）
  // ----------------------------------------------------------

  /**
   * 玩家或 AI 送出動作。
   *
   * 依當前 phase 分流：
   * - turn: 接受 currentSeat 的 discard / kong-concealed / kong-added / hu
   * - waiting_reaction: 接受 pendingReactionSeats 的 chi / pong / kong-exposed / hu / pass
   *
   * 違規（座位不對、動作不合法）會丟錯，caller 應確保輸入合法。
   */
  submitAction(seat: TSeat, action: TPlayerAction): void {
    this.bus.emit('player.action', { seat, action });

    if (this.state.phase === RoundPhase.InitialKong) {
      this.handleInitialKongAction(seat, action);
    } else if (this.state.phase === RoundPhase.Turn) {
      this.handleTurnAction(seat, action);
    } else if (this.state.phase === RoundPhase.WaitingReaction) {
      this.handleReactionAction(seat, action);
    } else {
      throw new Error(`submitAction called in invalid phase: ${this.state.phase}`);
    }
  }

  // ----------------------------------------------------------
  // Turn 階段：當前 seat 的動作
  // ----------------------------------------------------------

  private handleTurnAction(seat: TSeat, action: TPlayerAction): void {
    if (seat !== this.state.currentSeat) {
      throw new Error(`Turn action from non-current seat ${seat}`);
    }

    switch (action.type) {
      case PlayerActionType.Hu: {
        // 自摸胡：winTile = 最近摸進的牌（beginTurn / 槓後補牌時記在 state.lastDrawnTile）
        const winTile = this.state.lastDrawnTile;
        if (!winTile) throw new Error(`Cannot hu: no drawn tile recorded for seat ${seat}`);
        this.endRoundHu([{ seat, isSelfDraw: true, loserSeat: null, winTile }]);
        return;
      }
      case PlayerActionType.Discard: {
        this.applyDiscard(seat, action.tile, action.declareTenpai === true);
        return;
      }
      case PlayerActionType.KongConcealed: {
        // 暗槓：4 張同種從 concealed 移到 melds.kong-concealed，再抓替補
        const tiles = action.option.handTiles;
        let h = this.state.hands[seat];
        for (const t of tiles) h = removeFromConcealed(h, t);
        const meld: TMeld = { type: MeldType.KongConcealed, tiles: [...tiles] };
        h = addMeld(h, meld);
        this.state.hands[seat] = h;
        this.bus.emit('meld.formed', { seat, meld });
        this.state.wall = recordCompletedKong(this.state.wall);

        // 替補一張，繼續同 turn（槓後補牌 → 槓上開花旗標）
        const replacement = this.drawWithFlowerReplacement(seat, true);
        if (replacement === null) {
          if (!this.isRoundEnded()) this.endRoundDraw();
          return;
        }
        this.state.hands[seat] = addToConcealed(this.state.hands[seat], replacement);
        this.bus.emit('tile.drawn', {
          seat,
          tile: replacement,
          remaining: liveWallRemaining(this.state.wall),
        });
        this.emitTurnActions(seat, replacement);
        return;
      }
      case PlayerActionType.KongAdded: {
        this.declareAddedKong(seat, action.option);
        return;
      }
      case 'declare-tenpai': {
        this.state.declaredTenpai.add(seat);
        this.bus.emit('tenpai.declared', { seat });
        return;
      }
      default:
        throw new Error(`Unsupported turn action: ${action.type}`);
    }
  }

  /**
   * 宣告加槓：
   * - 先把第四張推出並檢查其他三家是否可胡。
   * - 有人可胡 → 進 WaitingReaction，只允許胡／過。
   * - 無人可胡 → 立即完成加槓並補牌。
   */
  private declareAddedKong(seat: TSeat, option: TKongAddedOption): void {
    const drawnTile = this.state.lastDrawnTile;
    const legal =
      drawnTile === null ? null : findKongAddedOption(this.state.hands[seat], drawnTile);
    if (
      !legal ||
      legal.pongMeldIndex !== option.pongMeldIndex ||
      legal.drawnTile.id !== option.drawnTile.id
    ) {
      throw new Error(`kong-added: illegal option for seat ${seat}`);
    }

    const pendingReactions = new Map<
      TSeat,
      { reactions: readonly TDiscardReaction[]; canHu: boolean }
    >();
    for (let s = 0; s < 4; s++) {
      const otherSeat = s as TSeat;
      if (otherSeat === seat) continue;
      if (canHu(this.state.hands[otherSeat], option.drawnTile, false)) {
        pendingReactions.set(otherSeat, { reactions: [], canHu: true });
      }
    }

    this.state.pendingAddedKong = { seat, option };
    this.state.pendingDiscard = null;
    this.bus.emit('kong.added-declared', { seat, tile: option.drawnTile });

    if (pendingReactions.size === 0) {
      this.finalizeAddedKong(seat, option);
      return;
    }

    this.state.phase = RoundPhase.WaitingReaction;
    this.beginReactionCollection(seat, pendingReactions);
  }

  /** 無人搶槓後，把原碰升級成加槓、摸一張槓後補牌並繼續原家的 turn。 */
  private finalizeAddedKong(seat: TSeat, option: TKongAddedOption): void {
    const oldMeld = this.state.hands[seat].melds[option.pongMeldIndex];
    if (!oldMeld || oldMeld.type !== MeldType.Pong) {
      throw new Error(`kong-added: invalid meld index ${option.pongMeldIndex}`);
    }

    const newMeld: TMeld = {
      type: MeldType.KongAdded,
      tiles: [...oldMeld.tiles, option.drawnTile],
      ...(oldMeld.fromSeat !== undefined ? { fromSeat: oldMeld.fromSeat } : {}),
    };
    const newMelds = [...this.state.hands[seat].melds];
    newMelds[option.pongMeldIndex] = newMeld;
    const handWithoutAddedTile = removeFromConcealed(this.state.hands[seat], option.drawnTile);
    this.state.hands[seat] = { ...handWithoutAddedTile, melds: newMelds };
    this.state.currentSeat = seat;
    this.state.phase = RoundPhase.Turn;
    this.clearPendingReactionState();
    this.bus.emit('meld.formed', { seat, meld: newMeld });
    this.state.wall = recordCompletedKong(this.state.wall);

    const replacement = this.drawWithFlowerReplacement(seat, true);
    if (replacement === null) {
      if (!this.isRoundEnded()) this.endRoundDraw();
      return;
    }
    this.state.hands[seat] = addToConcealed(this.state.hands[seat], replacement);
    this.bus.emit('tile.drawn', {
      seat,
      tile: replacement,
      remaining: liveWallRemaining(this.state.wall),
    });
    this.emitTurnActions(seat, replacement);
  }

  // ----------------------------------------------------------
  // 出牌：把牌進捨牌池、列出他家可反應動作
  // ----------------------------------------------------------

  private applyDiscard(seat: TSeat, tile: TTile, declareTenpai: boolean): void {
    // 從 concealed 拿掉
    const handAfterDiscard = removeFromConcealed(this.state.hands[seat], tile);
    const shouldDeclareTenpai = declareTenpai && !this.state.declaredTenpai.has(seat);
    if (shouldDeclareTenpai && findTenpaiTiles(handAfterDiscard).length === 0) {
      throw new Error(`Cannot declare tenpai: seat ${seat} is not in tenpai after discard`);
    }

    this.state.hands[seat] = handAfterDiscard;
    this.state.discards[seat].push(tile);
    this.bus.emit('tile.discarded', { seat, tile });

    if (shouldDeclareTenpai) {
      this.state.declaredTenpai.add(seat);
      this.bus.emit('tenpai.declared', { seat });
    }

    // 出完牌處於聽牌偵測時機（concealed 數=「等一張」狀態）
    this.emitTenpaiChanged(seat);

    // 收集其他 3 家對這張牌的可反應動作（canHu 每家只算一次，連同 reactions 一起存）
    const pendingReactions = new Map<
      TSeat,
      { reactions: readonly TDiscardReaction[]; canHu: boolean }
    >();
    for (let s = 0; s < 4; s++) {
      const otherSeat = s as TSeat;
      if (otherSeat === seat) continue;
      const reactions = findDiscardReactions(this.state.hands[otherSeat], tile, seat, otherSeat);
      const canHuHere = canHu(this.state.hands[otherSeat], tile, false);
      if (reactions.length > 0 || canHuHere) {
        pendingReactions.set(otherSeat, { reactions, canHu: canHuHere });
      }
    }

    if (pendingReactions.size === 0) {
      // 沒人反應，直接下一家
      this.bus.emit('discard.committed', { seat, tile });
      this.beginTurn(nextSeat(seat));
      return;
    }

    // 進入 waiting_reaction
    this.state.phase = RoundPhase.WaitingReaction;
    this.state.pendingDiscard = { seat, tile };
    this.bus.emit('discard.pending', { seat, tile });
    this.beginReactionCollection(seat, pendingReactions);
  }

  // ----------------------------------------------------------
  // Reaction 階段：先逐家攔胡，再收集一般副露
  // ----------------------------------------------------------

  private beginReactionCollection(
    originSeat: TSeat,
    reactions: Map<TSeat, { reactions: readonly TDiscardReaction[]; canHu: boolean }>
  ): void {
    this.state.pendingReactions = reactions;
    this.state.receivedReactions = new Map();
    this.state.pendingHuSeats = orderedReactionSeats(
      originSeat,
      [...reactions.entries()].filter(([, legal]) => legal.canHu).map(([seat]) => seat)
    ) as TSeat[];
    this.state.pendingHuIndex = 0;
    this.state.pendingMeldSeats = [];
    this.state.pendingMeldIndex = 0;

    if (this.state.pendingHuSeats.length > 0) {
      this.activateCurrentHuCandidate();
      return;
    }
    this.beginMeldReactionCollection();
  }

  private activateCurrentHuCandidate(): void {
    const seat = this.state.pendingHuSeats[this.state.pendingHuIndex];
    if (seat === undefined) {
      this.beginMeldReactionCollection();
      return;
    }
    this.state.pendingReactionSeats = new Set([seat]);
    this.state.receivedReactions = new Map();
    this.bus.emit('action.available', { seat, reactions: [], canHu: true });
  }

  private beginMeldReactionCollection(): void {
    this.state.pendingHuIndex = this.state.pendingHuSeats.length;
    this.state.receivedReactions = new Map();

    if (this.state.pendingAddedKong) {
      this.state.pendingReactionSeats = new Set();
      this.resolveAddedKongReactions();
      return;
    }

    const originSeat = this.state.pendingDiscard?.seat;
    if (originSeat === undefined) {
      throw new Error('beginMeldReactionCollection called with no pendingDiscard');
    }
    this.state.pendingMeldSeats = [
      ...orderedMeldReactionSeats(originSeat, this.state.pendingReactions),
    ];
    this.state.pendingMeldIndex = 0;
    this.activateCurrentMeldCandidate();
  }

  private activateCurrentMeldCandidate(): void {
    const seat = this.state.pendingMeldSeats[this.state.pendingMeldIndex];
    if (seat === undefined) {
      this.resolveReactions();
      return;
    }
    const legal = this.state.pendingReactions.get(seat);
    if (!legal) {
      throw new Error(`Missing pending reaction options for seat ${seat}`);
    }
    this.state.pendingReactionSeats = new Set([seat]);
    this.state.receivedReactions = new Map();
    this.bus.emit('action.available', {
      seat,
      reactions: [...legal.reactions],
      canHu: false,
    });
  }

  private isHuWindowActive(): boolean {
    return this.state.pendingHuIndex < this.state.pendingHuSeats.length;
  }

  private handleReactionAction(seat: TSeat, action: TPlayerAction): void {
    if (!this.state.pendingReactionSeats.has(seat)) {
      throw new Error(`Reaction from seat ${seat} not in pending list`);
    }
    if (this.state.receivedReactions.has(seat)) {
      throw new Error(`Reaction from seat ${seat} already submitted`);
    }
    const legal = this.state.pendingReactions.get(seat);
    const isHuWindow = this.isHuWindowActive();
    if (
      !legal ||
      (isHuWindow
        ? action.type !== PlayerActionType.Hu && action.type !== PlayerActionType.Pass
        : !this.isLegalReaction({ ...legal, canHu: false }, action))
    ) {
      throw new Error(`Illegal reaction ${action.type} from seat ${seat}`);
    }

    if (isHuWindow) {
      if (action.type === PlayerActionType.Hu) {
        this.state.receivedReactions.set(seat, action);
        this.resolveReactions();
        return;
      }
      this.state.pendingHuIndex += 1;
      this.activateCurrentHuCandidate();
      return;
    }

    if (action.type === PlayerActionType.Pass) {
      this.state.pendingMeldIndex += 1;
      this.activateCurrentMeldCandidate();
      return;
    }

    // 只有目前受詢者能送出非放棄動作；確定吃／碰／槓後立即落定，不再詢問後位。
    this.state.receivedReactions.set(seat, action);
    this.resolveReactions();
  }

  /**
   * 驗證某家送出的反應動作是否在其合法清單內。
   * - pass 永遠合法；hu 需該家 canHu；chi/pong/kong-exposed 需出現在 reactions 清單。
   * - turn 動作（discard/kong-concealed…）不該出現在反應階段，一律不合法。
   */
  private isLegalReaction(
    legal: { reactions: readonly TDiscardReaction[]; canHu: boolean },
    action: TPlayerAction
  ): boolean {
    switch (action.type) {
      case PlayerActionType.Pass:
        return true;
      case PlayerActionType.Hu:
        return legal.canHu;
      case PlayerActionType.Chi:
      case PlayerActionType.Pong:
      case PlayerActionType.KongExposed:
        return legal.reactions.some((r) => r.type === action.type);
      default:
        return false;
    }
  }

  /**
   * 解析 reactions 並決定下一步：
   * 1. 順位攔胡成立 → 唯一 winner → round_end_hu
   * 2. 否則任一 pong/kong-exposed → 該家形成副露、進入該家 turn
   * 3. 否則任一 chi → 該家形成副露、進入該家 turn
   * 4. 全 pass → 下一家 turn
   */
  private resolveReactions(): void {
    if (this.state.pendingAddedKong) {
      this.resolveAddedKongReactions();
      return;
    }

    const discardInfo = this.state.pendingDiscard;
    if (!discardInfo) {
      throw new Error('resolveReactions called with no pendingDiscard');
    }

    // 優先序由純函式決定（胡 > 碰/明槓 > 吃 > 全過）；以下只負責套用狀態變化。
    const decision = resolveReactionPriority(this.state.receivedReactions, this.state.hands);

    switch (decision.kind) {
      case 'hu': {
        const huInfos: THuInfo[] = [
          {
            seat: decision.seat,
            isSelfDraw: false,
            loserSeat: discardInfo.seat,
            winTile: discardInfo.tile,
          },
        ];
        this.endRoundHu(huInfos);
        return;
      }
      case 'pong':
        this.applyMeldFromDiscard(
          decision.seat,
          {
            type: MeldType.Pong,
            tiles: [...decision.option.handTiles, discardInfo.tile],
            fromSeat: discardInfo.seat,
          },
          decision.option.handTiles
        );
        return;
      case 'kong-exposed': {
        this.applyMeldFromDiscard(
          decision.seat,
          {
            type: MeldType.KongExposed,
            tiles: [...decision.option.handTiles, discardInfo.tile],
            fromSeat: discardInfo.seat,
          },
          decision.option.handTiles,
          false
        );
        this.state.wall = recordCompletedKong(this.state.wall);
        // 明槓後該家補一張並繼續 turn（同 turn 處理；槓後補牌 → 槓上開花旗標）
        const replacement = this.drawWithFlowerReplacement(decision.seat, true);
        if (replacement === null) {
          if (!this.isRoundEnded()) this.endRoundDraw();
          return;
        }
        this.state.hands[decision.seat] = addToConcealed(
          this.state.hands[decision.seat],
          replacement
        );
        this.bus.emit('tile.drawn', {
          seat: decision.seat,
          tile: replacement,
          remaining: liveWallRemaining(this.state.wall),
        });
        this.emitTurnActions(decision.seat, replacement);
        return;
      }
      case 'chi':
        this.applyMeldFromDiscard(
          decision.seat,
          {
            type: MeldType.Chi,
            tiles: [...decision.option.sequence],
            fromSeat: discardInfo.seat,
          },
          decision.option.handTiles
        );
        return;
      case 'pass':
        this.bus.emit('discard.committed', {
          seat: discardInfo.seat,
          tile: discardInfo.tile,
        });
        this.clearPendingReactionState();
        this.beginTurn(nextSeat(discardInfo.seat));
        return;
    }
  }

  /** 解析搶槓窗口：胡優先；全部放棄才正式完成加槓。 */
  private resolveAddedKongReactions(): void {
    const pending = this.state.pendingAddedKong;
    if (!pending) throw new Error('resolveAddedKongReactions called with no pending kong');

    const decision = resolveReactionPriority(this.state.receivedReactions, this.state.hands);
    if (decision.kind === 'hu') {
      // 推出的第四張被胡走：原碰維持不變，第四張離開宣告者的暗牌。
      this.state.hands[pending.seat] = removeFromConcealed(
        this.state.hands[pending.seat],
        pending.option.drawnTile
      );
      const huInfos: THuInfo[] = [
        {
          seat: decision.seat,
          isSelfDraw: false,
          loserSeat: pending.seat,
          winTile: pending.option.drawnTile,
          isRobbingKong: true,
        },
      ];
      // 與放槍胡保留 pendingDiscard 同理：結束狀態保留被搶的第四張來源，
      // 供重播／牌張守恆讀取；只清掉已完成的反應收集資料。
      this.state.pendingReactionSeats.clear();
      this.state.pendingReactions.clear();
      this.state.receivedReactions.clear();
      this.endRoundHu(huInfos);
      return;
    }

    if (decision.kind !== 'pass') {
      throw new Error(`Unexpected added-kong reaction: ${decision.kind}`);
    }
    this.finalizeAddedKong(pending.seat, pending.option);
  }

  /** 清空捨牌／搶槓共用的反應暫存。 */
  private clearPendingReactionState(): void {
    this.state.pendingDiscard = null;
    this.state.pendingAddedKong = null;
    this.state.pendingReactionSeats.clear();
    this.state.pendingReactions.clear();
    this.state.receivedReactions.clear();
    this.state.pendingHuSeats = [];
    this.state.pendingHuIndex = 0;
    this.state.pendingMeldSeats = [];
    this.state.pendingMeldIndex = 0;
  }

  /**
   * 套用一個從捨牌觸發的副露：
   * 1. 從 hand.concealed 移除組成牌中屬於自家的部份
   * 2. 把整個 meld 加進 hand.melds
   * 3. 把捨牌從 discards 拿掉（不重複出現於牌池）
   * 4. 設定 currentSeat = 該家、phase = 'turn'
   * 5. 該家必須出牌（caller 透過 turn 動作流程處理）
   */
  private applyMeldFromDiscard(
    seat: TSeat,
    meld: TMeld,
    handTilesUsed: readonly TTile[],
    emitActionAvailable = true
  ): void {
    // 移除自家實際持有的組成牌（由 reaction option 明確指定，
    // 不可從 meld.tiles 推 —— 吃的 sequence 已依 rank 排序，捨牌不一定在尾端）
    let h = this.state.hands[seat];
    for (const t of handTilesUsed) {
      h = removeFromConcealed(h, t);
    }
    h = addMeld(h, meld);
    this.state.hands[seat] = h;

    // 從 discards 拿掉最後那張（剛被吃/碰/槓走）
    const fromSeat = meld.fromSeat;
    if (fromSeat !== undefined) {
      const discardPile = this.state.discards[fromSeat];
      const lastDiscard = discardPile[discardPile.length - 1];
      if (lastDiscard && meld.tiles.some((tile) => tile.id === lastDiscard.id)) {
        discardPile.pop();
      }
    }

    // 副露完該家負責出牌（不再抓牌）
    this.state.currentSeat = seat;
    this.state.phase = RoundPhase.Turn;
    this.state.lastDrawnTile = null;
    this.state.lastDrawnSeat = null;
    this.state.lastDrawWasKong = false;
    this.clearPendingReactionState();

    // 狀態切換完成後才發布結果事件，確保 UI 在 meld.formed listener 內已能把該家視為可出牌。
    this.bus.emit('meld.formed', { seat, meld });

    // 吃／碰不抓牌，直接暴露該家的出牌動作；明槓須等補牌完成後由 emitTurnActions 發一次。
    if (emitActionAvailable) {
      this.bus.emit('action.available', {
        seat,
        reactions: [],
        canHu: false, // 吃/碰後立刻自摸胡的型很罕見，MVP 不開放
      });
    }
  }

  // ----------------------------------------------------------
  // 結算
  // ----------------------------------------------------------

  /** 某家是否已收齊八種花牌。 */
  private hasEightFlowers(seat: TSeat): boolean {
    return new Set(this.state.hands[seat].flowers.map((flower) => flower.rank)).size === 8;
  }

  /** 八仙過海：不檢查一般牌型，直接以特殊自摸結束牌局。 */
  private endRoundEightFlowers(seat: TSeat, winTile: TTile): void {
    this.endRoundHu([
      {
        seat,
        isSelfDraw: true,
        loserSeat: null,
        winTile,
        specialWin: SpecialWin.EightFlowers,
      },
    ]);
  }

  private endRoundHu(huInfos: ReadonlyArray<THuInfo>): void {
    if (huInfos.length !== 1) {
      throw new Error(`Round summary must contain exactly one winner, got ${huInfos.length}`);
    }
    this.state.phase = RoundPhase.RoundEndHu;

    const results = computeHuResults(huInfos, {
      hands: this.state.hands,
      dealerSeat: this.state.config.dealerSeat,
      seatWinds: this.state.config.seatWinds,
      prevailingWind: this.state.config.prevailingWind,
      declaredTenpai: this.state.declaredTenpai,
      dealerStreak: this.state.config.dealerStreak ?? 0,
      wallRemaining: liveWallRemaining(this.state.wall),
      isAfterKong: this.state.lastDrawWasKong,
    });

    const summary: TRoundSummary = { kind: RoundSummaryKind.Hu, results };
    this.bus.emit('round.ended', { summary });
  }

  private endRoundDraw(): void {
    this.state.phase = RoundPhase.RoundEndDraw;
    this.bus.emit('round.draw', {});
    this.bus.emit('round.ended', { summary: { kind: RoundSummaryKind.Draw } });
  }

  /**
   * 對某座位重新偵測聽牌並發出事件。
   * UI / AI 訂閱這個事件後可顯示聽牌提示或調整策略。
   * 不在聽牌時也會發空陣列，UI 端可據此清除提示。
   */
  private emitTenpaiChanged(seat: TSeat): void {
    const tenpaiTiles = findTenpaiTiles(this.state.hands[seat]);
    this.bus.emit('tenpai.changed', { seat, listeningTiles: tenpaiTiles });
  }
}

// ============================================================
// 不直接 export 的內部 helper（保留導出以便測試訪問）
// ============================================================

export const _internal = {
  nextSeat,
};
